import React, { useState } from "react";
import axios from "axios";
import { X, Search } from "lucide-react";
import { Link } from "react-router-dom";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const SearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query) return;
    setLoading(true);

    try {
      const res = await axios.get(
        `${SERVER_URL}/api/products/search?query=${query}`
      );
      setResults(res.data);
    } catch (error) {
      console.error("Search error:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-start pt-20 z-[60] transition-opacity duration-300">
      <div className="bg-white w-full max-w-xl max-h-[80vh] mx-4 p-6 rounded-2xl shadow-2xl relative overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Search Products</h2>
          <button
            className="text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-200 p-1 rounded-full transition-colors"
            onClick={onClose}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSearch} className="flex mb-6 shadow-sm rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-yellow-400 transition-shadow">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="What are you looking for?"
            className="flex-grow bg-gray-50 border-0 px-5 py-3.5 focus:outline-none text-gray-900 placeholder-gray-400"
            autoFocus
          />
          <button
            type="submit"
            className="bg-yellow-500 text-black font-semibold px-6 py-3.5 hover:bg-yellow-600 transition-colors flex items-center gap-2"
          >
            <Search size={18} /> <span className="hidden sm:inline">Search</span>
          </button>
        </form>

        {/* Scrollable Results Container */}
        <div className="overflow-y-auto max-h-[50vh] pr-2 -mr-2">
          {loading ? (
            <div className="flex justify-center items-center space-x-3 py-10 text-yellow-600">
              <svg
                className="animate-spin h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
              <span className="text-sm font-medium animate-pulse">
                Finding the best fits...
              </span>
            </div>
          ) : results.length > 0 ? (
            <ul className="space-y-2">
              {results.map((product) => (
                <li key={product._id} className="group">
                  <Link
                    to={`/product/${product._id}`}
                    onClick={onClose}
                    className="flex items-center space-x-4 hover:bg-yellow-50 p-3 rounded-xl transition-colors"
                  >
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-14 h-14 object-cover rounded-md border border-gray-100 shadow-sm"
                    />
                    <div className="flex-1">
                      <p className="text-base font-semibold text-gray-900 group-hover:text-yellow-700 transition-colors">
                        {product.name}
                      </p>
                      <p className="text-sm text-gray-500 py-0.5">
                        {product.category}
                      </p>
                    </div>
                    <span className="font-bold text-gray-900 bg-white px-3 py-1 border border-gray-100 rounded-lg shadow-sm">
                      Ksh {product.price.toLocaleString()}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            query && (
              <div className="py-10 text-center">
                <p className="text-gray-500 font-medium">No results found for "{query}".</p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
